/**
 * Builtin + mock-external Component Registry (frontend).
 * Registry owns runtime/permissions/bundle — canvas nodes cannot override.
 */

export const RUNTIME_BUILTIN = 'builtin';
export const RUNTIME_SANDBOX = 'sandbox-iframe';

function defn(id, version, opts = {}) {
  return {
    id,
    version,
    displayName: opts.displayName || id,
    acceptsChildren: !!opts.acceptsChildren,
    // Open by default — agents may pass any MUI v4 prop (incl. style/className/classes).
    propsSchema: opts.propsSchema || { type: 'object', additionalProperties: true },
    source: opts.source || 'builtin',
    runtime: opts.runtime || RUNTIME_BUILTIN,
    permissions: opts.permissions || ['theme', 'props', 'data'],
    bundle: opts.bundle || null,
    approved: opts.approved !== false,
    runtimeImplemented: opts.runtimeImplemented !== false,
    // React component assigned in registerReact()
    Component: opts.Component || null,
  };
}

const catalog = new Map();

function key(id, version) {
  return `${id}@${version}`;
}

export function registerDefinition(definition) {
  catalog.set(key(definition.id, definition.version), definition);
}

export function registerReact(id, version, Component) {
  const d = catalog.get(key(id, version));
  if (d) d.Component = Component;
}

function seed() {
  if (catalog.size) return;
  const builtins = [
    defn('builtin:Stack', '1.0.0', { displayName: 'Stack', acceptsChildren: true }),
    defn('builtin:Grid', '1.0.0', { displayName: 'Grid', acceptsChildren: true }),
    defn('builtin:Box', '1.0.0', { displayName: 'Box', acceptsChildren: true }),
    defn('builtin:Divider', '1.0.0', { displayName: 'Divider' }),
    defn('builtin:Spacer', '1.0.0', { displayName: 'Spacer' }),
    defn('builtin:Typography', '1.0.0', { displayName: 'Typography' }),
    defn('builtin:Banner', '1.0.0', { displayName: 'Banner' }),
    defn('builtin:MetricCard', '1.0.0', { displayName: 'Metric Card' }),
    defn('builtin:List', '1.0.0', { displayName: 'List' }),
    defn('builtin:DataTable', '1.0.0', { displayName: 'Data Table' }),
    defn('builtin:Image', '1.0.0', { displayName: 'Image' }),
    defn('builtin:Link', '1.0.0', { displayName: 'Link' }),
    defn('builtin:LineChart', '1.0.0', { displayName: 'Line Chart' }),
    defn('builtin:BarChart', '1.0.0', { displayName: 'Bar Chart' }),
    defn('builtin:PieChart', '1.0.0', { displayName: 'Pie Chart' }),
    defn('builtin:Form', '1.0.0', { displayName: 'Form', acceptsChildren: true }),
    // Props are open: agents may pass any @material-ui/core v4 TextField prop
    // (variant, style, className, classes, InputProps, etc.). Canvas strips only
    // runtime keys (statePath). See passthroughMuiProps.
    defn('builtin:TextField', '1.0.0', {
      displayName: 'Text Field',
      propsSchema: { type: 'object', additionalProperties: true },
    }),
    defn('builtin:Select', '1.0.0', { displayName: 'Select' }),
    defn('builtin:Checkbox', '1.0.0', { displayName: 'Checkbox' }),
    defn('builtin:Button', '1.0.0', { displayName: 'Button' }),
    defn('external:mock/StatusTimeline', '1.0.0', {
      displayName: 'Status Timeline (mock external)',
      source: 'external',
      runtime: RUNTIME_SANDBOX,
      permissions: ['theme', 'props', 'data', 'state.read', 'actions.emit'],
      bundle: {
        storage_key: 'components/mock/StatusTimeline/1.0.0/index.html',
        integrity: 'sha256:deadbeef',
        entry: 'index.html',
      },
      approved: true,
      runtimeImplemented: false,
    }),
  ];
  builtins.forEach(registerDefinition);
}

seed();

export function resolve(componentId, version = '1.0.0') {
  return catalog.get(key(componentId, version)) || null;
}

export function listDefinitions(filterSource) {
  const all = Array.from(catalog.values());
  if (!filterSource) return all;
  return all.filter((d) => d.source === filterSource);
}

export function isPublishable(componentId, version, { required = true } = {}) {
  const d = resolve(componentId, version);
  if (!d) return { ok: false, message: `unknown component ${componentId}@${version}` };
  if (!d.approved) return { ok: false, message: `component ${componentId}@${version} is not approved` };
  if (required && !d.runtimeImplemented) {
    return {
      ok: false,
      message: `component ${componentId}@${version} runtime is not implemented; cannot publish as required`,
    };
  }
  return { ok: true, message: '' };
}
